<template>
    <div class="mt-4 lg:mt-6">
        <template v-if="!verifying">
            <span class="text-slate-900 font-semibold text-sm block mb-2 sm:text-base">
                Enter a list of phone numbers
            </span>

            <vx-input :rows="6" v-model="phoneNumbersRaw"></vx-input>

            <ul class="mt-2 list-disc pl-4 text-slate-500 text-xs">
                <li>Numbers can be one per line or comma separated.</li>
                <li>You should have explicit permission to text these numbers or risk service disruption.</li>
                <li>Max {{ MAX_PHONE_NUMBERS }} numbers for manual entry.</li>
            </ul>
            <vx-button
                :disabled="processing"
                class="mt-4 lg:mt-6"
                size="lg"
                :block="true"
                @click="verifyPhoneNumbers()"
                data-test="manual-verify"
            >
                {{ processing ? 'Verifying' : 'Verify Numbers' }}
                <font-awesome-icon
                    widthAuto
                    class="text-base"
                    :icon="processing ? faSpinnerThird : faShieldCheck"
                    :spin="processing"
                ></font-awesome-icon>
            </vx-button>
        </template>
        <template v-else>
            <div
                :class="{
                    'pointer-events-none': processing,
                }"
            >
                <div
                    class="border border-solid border-slate-200 rounded-2xl w-full py-4 px-6 text-sm text-slate-600 font-semibold flex justify-between items-center"
                >
                    {{ validContacts.length }} new + existing
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </div>
                <div
                    v-if="!invalidPhoneNumbers.length"
                    class="border border-solid border-slate-200 rounded-2xl w-full py-4 px-6 text-sm text-slate-600 font-semibold flex justify-between items-center mt-1"
                >
                    {{ invalidPhoneNumbers.length }} invalid
                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                </div>
                <vx-disclosure v-else class="mt-1" :active="true">
                    <template v-slot:header>
                        <span class="text-rose-900"> {{ invalidPhoneNumbers.length }} invalid </span>
                    </template>
                    <template v-slot:content>
                        <p class="text-slate-700 mb-4 text-sm">
                            The following numbers will be excluded from the blast if not corrected.
                        </p>

                        <div
                            v-for="phoneNumber in invalidPhoneNumbers"
                            :key="phoneNumber.key"
                            class="flex gap-1 items-center mb-3 flex-wrap w-full last:mb-0 lg:flex-nowrap lg:gap-3"
                            data-test="manual-invalid-row"
                        >
                            <div class="relative w-[calc(100%-2.25rem)] lg:w-36">
                                <input
                                    type="text"
                                    class="appearance-none border border-solid border-slate-300 rounded-md h-8 pl-3 pr-6 text-slate-700 text-sm font-sans w-full"
                                    v-model="phoneNumber.phoneNumber"
                                    @blur="updateInvalid(phoneNumber)"
                                />

                                <button
                                    role="button"
                                    class="appearance-none border-none bg-transparent p-0 absolute inset-bs-[5px] inset-e-3"
                                    @click="updateInvalid(phoneNumber)"
                                    data-test="manual-invalid-row-update"
                                >
                                    <font-awesome-icon widthAuto :icon="faPenToSquare"></font-awesome-icon>
                                </button>
                            </div>
                            <button
                                role="button"
                                class="appearance-none border-none bg-transparent p-0 w-8 lg:grow-0"
                                @click="removeInvalid(phoneNumber)"
                                data-test="manual-invalid-row-remove"
                            >
                                <font-awesome-icon widthAuto :icon="faTrashCan"></font-awesome-icon>
                            </button>
                            <span
                                class=""
                                :class="{
                                    'text-rose-900': phoneNumber.reason,
                                    'text-teal-600': !phoneNumber.reason,
                                }"
                            >
                                {{ phoneNumber.reason || 'Updated' }}
                            </span>
                        </div>
                    </template>
                </vx-disclosure>
            </div>
            <div class="flex gap-2 mt-4 lg:mt-6">
                <vx-button
                    :disabled="processing"
                    class="w-28"
                    size="lg"
                    color="muted"
                    @click="reset()"
                    data-test="manual-reset"
                >
                    Reset
                    <font-awesome-icon widthAuto class="text-base" :icon="faArrowRotateLeft"></font-awesome-icon>
                </vx-button>
                <vx-button
                    v-if="canReverify && invalidPhoneNumbers.length"
                    :disabled="processing"
                    size="lg"
                    :block="true"
                    @click="reverifyPhoneNumbers()"
                    data-test="manual-reverify"
                >
                    {{ processing ? 'Verifying' : 'Reverify' }}
                    <font-awesome-icon
                        widthAuto
                        class="text-base"
                        :icon="processing ? faSpinnerThird : faShieldCheck"
                        :spin="processing"
                    ></font-awesome-icon>
                </vx-button>
                <vx-button
                    v-else
                    size="lg"
                    :disabled="processing"
                    :block="true"
                    @click="confirm()"
                    data-test="manual-continue"
                >
                    Continue
                    <font-awesome-icon widthAuto class="text-base" :icon="faArrowTurnDown"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </div>
</template>

<script setup>
import { VxDisclosure, VxButton, VxInput } from '@voxie/frontend-components';
import { ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faShieldCheck,
    faArrowRotateLeft,
    faArrowTurnDown,
    faPenToSquare,
    faCircleCheck,
    faSpinnerThird,
} from '@fortawesome/pro-solid-svg-icons';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { contactsClient, actionsClient } from '~/services';
import { useToasted } from '../../composables/useToasted';
import { e164Format, isValidPhoneNumber } from '~/utils/phoneNumber';
import {
    AUDIENCE_TYPE_MARKETING,
    AUDIENCE_TYPE_TRANSACTIONAL,
    MAX_CONTACTS_PER_REQUEST as MAX_PHONE_NUMBERS,
} from '~/constants/campaign';

const teamId = Spark.state.currentTeam.id;

const toasted = useToasted();

const phoneNumbersRaw = ref('');

const props = defineProps({
    modelValue: {
        type: Array,
        required: false,
        default: () => [],
    },
    campaignType: {
        type: String,
        required: true,
        validator: (campaignType) => [AUDIENCE_TYPE_MARKETING, AUDIENCE_TYPE_TRANSACTIONAL].includes(campaignType),
    },
});

const emit = defineEmits(['update:modelValue', 'continue']);

const verifying = ref(Boolean(props.modelValue.length));
const processing = ref(false);
const canReverify = ref(false);

const validContacts = ref(props.modelValue);
const invalidPhoneNumbers = ref([]);

watch(
    () => props.modelValue,
    (newModelValue) => {
        validContacts.value = newModelValue;
    }
);

watch(validContacts, (newValidContacts) => {
    emit('update:modelValue', newValidContacts);
});

/**
 * @param {{key: number, reason: string, phoneNumber: string}} phoneNumber
 */
const updateInvalid = (phoneNumber) => {
    canReverify.value = true;

    invalidPhoneNumbers.value = invalidPhoneNumbers.value.map((item) => {
        if (item.key == phoneNumber.key) {
            item.reason = null;
            return item;
        }

        return item;
    });
};

/**
 * @param {{key: number, reason: string, phoneNumber: string}} phoneNumber
 */
const removeInvalid = (phoneNumber) => {
    invalidPhoneNumbers.value = invalidPhoneNumbers.value.filter((item) => item.key !== phoneNumber.key);
};

const confirm = async () => {
    emit('continue', validContacts.value);
};

const reset = () => {
    verifying.value = false;
    processing.value = false;
    canReverify.value = false;

    validContacts.value = [];
    invalidPhoneNumbers.value = [];
};

/**
 * Takes the content from the textarea and processes them
 */
const verifyPhoneNumbers = () => {
    if (!phoneNumbersRaw.value.length) {
        toasted.error('Please enter a list of phone numbers');
        return;
    }

    const phoneNumbers = phoneNumbersRaw.value
        .split(/\r?\n|\r|\n|,|;/g)
        .filter((phoneNumberRaw) => phoneNumberRaw?.trim?.()?.length);

    processPhoneNumbers(phoneNumbers);
};

/**
 * Takes the invalid phone numbers that have been corrected and reprocess them
 */
const reverifyPhoneNumbers = () => {
    const phoneNumbers = invalidPhoneNumbers.value.map((item) => item.phoneNumber);
    processPhoneNumbers(phoneNumbers);
};

/**
 *
 * @param {string[]} phoneNumbers
 */
const processPhoneNumbers = async (phoneNumbers) => {
    processing.value = true;
    canReverify.value = false;

    const currentInvalidPhoneNumbers = [];
    const initialValidContactsCount = validContacts.value.length;

    try {
        for (const [index, phoneNumber] of phoneNumbers.entries()) {
            if (index + initialValidContactsCount >= MAX_PHONE_NUMBERS) {
                currentInvalidPhoneNumbers.push({
                    reason: 'Max phone numbers reached',
                    key: Math.floor(Math.random() * 10000),
                    phoneNumber: phoneNumber,
                });
                continue;
            }

            const phoneNumberFormatted = e164Format(phoneNumber);

            if (!phoneNumberFormatted?.trim()?.length) {
                currentInvalidPhoneNumbers.push({
                    reason: 'Invalid format',
                    key: Math.floor(Math.random() * 10000),
                    phoneNumber: phoneNumber,
                });
                continue;
            }

            if (!isValidPhoneNumber(phoneNumberFormatted)) {
                currentInvalidPhoneNumbers.push({
                    reason: 'Invalid phone number',
                    key: Math.floor(Math.random() * 10000),
                    phoneNumber: phoneNumber,
                });
                continue;
            }

            const invalidContact = await processPhoneNumber(phoneNumberFormatted);
            if (invalidContact) {
                currentInvalidPhoneNumbers.push(invalidContact);
            }
        }
    } finally {
        processing.value = false;
        verifying.value = true;
        invalidPhoneNumbers.value = currentInvalidPhoneNumbers;
    }
};

const processPhoneNumber = async (phoneNumber) => {
    const contact = await lookupContact(phoneNumber);

    const contactAlreadyAdded = validContacts.value.find((contact) => contact.phone === phoneNumber);

    if (contactAlreadyAdded) {
        return null;
    }

    const subscriptionCheck = await checkSubscriptionStatus(contact.id);

    if (!subscriptionCheck.valid) {
        return {
            reason: subscriptionCheck.reason,
            key: Math.floor(Math.random() * 10000),
            phoneNumber: phoneNumber,
        };
    }

    validContacts.value = [...validContacts.value, contact];
    return null;
};

const checkSubscriptionStatus = async (contactId) => {
    try {
        const response = await contactsClient.getContactSubscriptionStatus(teamId, contactId);
        const subscriptions = response.data?.data || [];

        if (props.campaignType === AUDIENCE_TYPE_MARKETING) {
            const marketingSubscription = subscriptions.find((sub) => sub.type === 'marketing');

            if (!marketingSubscription || marketingSubscription.status !== 'opt_in') {
                return {
                    valid: false,
                    reason: 'Not opted in for marketing messages',
                };
            }
        } else if (props.campaignType === AUDIENCE_TYPE_TRANSACTIONAL) {
            const transactionalSubscription = subscriptions.find((sub) => sub.type === 'transactional');

            if (transactionalSubscription && transactionalSubscription.status === 'opt_out') {
                return {
                    valid: false,
                    reason: 'Opted out of transactional messages',
                };
            }
        }

        return { valid: true };
    } catch (e) {
        console.error(e);
        return {
            valid: false,
            reason: 'Unable to verify subscription status',
        };
    }
};

const lookupContact = async (phoneNumber) => {
    try {
        return await fetchContact(phoneNumber);
    } catch {
        return await createContact(phoneNumber);
    }
};

const fetchContact = async (phoneNumber) => {
    try {
        const response = await contactsClient.searchContacts(teamId, {
            'filter[phone]': phoneNumber,
        });

        if (response.data?.data?.length === 1 && response.data.data[0].phone === phoneNumber) {
            return response.data.data[0];
        }
    } catch (e) {
        console.error(e);
    }

    throw new Error(`It was not possible to fetch the contact with the phone number ${phoneNumber}.`);
};

const createContact = async (phoneNumber) => {
    try {
        const response = await contactsClient.contactSave(teamId, {
            phone: phoneNumber,
        });
        actionsClient.send('contact_added');
        return response.data;
    } catch {
        throw new Error(`It was not possible to create a new contact with the phone number ${phoneNumber}.`);
    }
};
</script>
